import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowRight, Play, Building2, Award, Heart, MapPin, Users, Zap } from 'lucide-react';
import { trackPageView } from '../utils/analytics';
import SEOHead from '../components/SEOHead';

const timeline = [
  { year: '2004', title: 'Foundation', description: 'Avi Cement established in Gujarat with a vision to deliver quality building materials.' },
  { year: '2008', title: 'First Expansion', description: 'Expanded production capacity to meet growing demand across North Gujarat.' },
  { year: '2012', title: 'Brand Launch', description: 'Launched Lion and Hathi cement brands, quickly gaining market trust.' },
  { year: '2016', title: 'AAC Division', description: 'Introduced AAC Blocks division for sustainable building solutions.' },
  { year: '2019', title: 'Cemtite Range', description: 'Launched complete Cemtite range of construction chemicals.' },
  { year: '2021', title: 'Rajasthan Unit', description: 'Opened second manufacturing unit, expanding regional presence.' },
  { year: '2024', title: '20 Years Strong', description: 'Celebrating two decades of building India with 500+ dealers.' },
];

const values = [
  { icon: Zap, title: 'Strength', description: 'Products engineered to exceed standards, ensuring structures that stand the test of time.', color: '#f36458' },
  { icon: Heart, title: 'Sustainability', description: 'Committed to eco-friendly practices, from flyash utilization to responsible sourcing.', color: '#10b981' },
  { icon: Award, title: 'Service', description: 'Dedicated support network ensuring your projects never face material delays.', color: '#3b82f6' },
];

const AboutPage = () => {
  useEffect(() => {
    trackPageView('/about', 'About Us');
  }, []);

  const [timelineRef, timelineInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [valuesRef, valuesInView] = useInView({ triggerOnce: true, threshold: 0.2 });

  return (
    <div className="min-h-screen bg-void" data-testid="about-page">
      <SEOHead
        title="About Us"
        description="Avi Cement Limited — Building India since 2004. 20+ years of manufacturing premium cement, AAC blocks, and construction chemicals with 2 manufacturing units in Gujarat & Rajasthan and 750+ authorized dealers."
        path="/about"
      />
      {/* Hero Section */}
      <section className="relative py-12 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <motion.div
          className="absolute top-1/2 right-0 w-[600px] h-[600px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.1) 0%, transparent 60%)' }}
        />
        
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="text-coral font-mono text-sm mb-4 block">ABOUT US</span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Built to Last.<br />
              <span className="text-gradient">Built for India.</span>
            </h1>
            <p className="text-gray-400 text-lg md:text-xl leading-relaxed">
              For over two decades, Avi Cement has been the foundation of countless dreams—from 
              towering structures to humble homes. This is our story.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Timeline Section */}
      <section ref={timelineRef} className="py-12 md:py-32 bg-surface" data-testid="timeline-section">
        <div className="container mx-auto px-4 md:px-8 max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={timelineInView ? { opacity: 1, y: 0 } : {}}
            className="text-center mb-16"
          >
            <span className="text-coral font-mono text-sm mb-4 block">OUR JOURNEY</span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white">
              Two decades of growth
            </h2>
          </motion.div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-white/10 transform md:-translate-x-1/2" />

            {timeline.map((item, index) => (
              <motion.div
                key={item.year}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                animate={timelineInView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: index * 0.1 }}
                className={`relative flex items-center mb-8 ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                {/* Timeline marker */}
                <div className="absolute left-4 md:left-1/2 w-3 h-3 bg-coral rounded-full transform -translate-x-1/2 z-10" />
                
                {/* Content */}
                <div className={`ml-12 md:ml-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                  <span className="font-mono text-coral text-lg">{item.year}</span>
                  <h3 className="font-display text-xl font-semibold text-white mt-1">{item.title}</h3>
                  <p className="text-gray-500 text-sm mt-2">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section ref={valuesRef} className="py-12 md:py-32 bg-void" data-testid="values-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={valuesInView ? { opacity: 1, y: 0 } : {}}
            className="text-center mb-16"
          >
            <span className="text-coral font-mono text-sm mb-4 block">OUR VALUES</span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white">
              What we stand for
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                animate={valuesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: index * 0.15 }}
                className="bg-surface border border-white/5 p-8 rounded-2xl text-center hover:border-white/20 transition-colors"
              >
                <div 
                  className="w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center"
                  style={{ backgroundColor: `${value.color}15` }}
                >
                  <value.icon className="w-8 h-8" style={{ color: value.color }} />
                </div>
                <h3 className="font-display text-xl font-semibold text-white mb-3">{value.title}</h3>
                <p className="text-gray-500 text-sm">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-surface border-t border-white/5">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-display text-2xl md:text-3xl font-bold text-white mb-2">
                Ready to work with us?
              </h3>
              <p className="text-gray-400">Partner with a brand built on trust and quality.</p>
            </div>
            <Link
              to="/distributors"
              className="group flex items-center gap-2 bg-coral hover:bg-coral-dark text-white font-display font-medium px-6 py-3 rounded-full transition-all"
            >
              Become a Partner <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
